import { useMemo } from 'react';

import type { TableColumn } from '../Table';

import type { FilterOperator } from './types';

/**
 * How a field's value is entered. `text` takes a free-text comparison, `enum` picks from the values
 * the data actually holds, and `date` bounds a range. `enum-one` is a single-choice enumeration —
 * the field's vocabulary is closed, but only one value can ever be true of a scalar query
 * parameter, unlike the checkbox list `enum` renders. `date-max` is a single upper bound rather
 * than a range, for an endpoint that takes one `…Before`-style parameter.
 *
 * `number` bounds a numeric range — a size, a duration — with the strict single bounds and
 * `between`; the unit is the page's to name in the field's label, and the page converts on the way
 * out (megabytes typed, bytes sent).
 *
 * `date`, `enum-one`, `date-max` and `number` are never inferred: a column of dates or a
 * single-choice vocabulary reads as free text or as a checkbox enumeration depending on how many
 * rows happen to repeat, and none of those readings is what the underlying parameter wants. A page
 * that needs one of these declares it through `overrides.types` or `overrides.extra`.
 */
export type TableFilterFieldType = 'text' | 'enum' | 'date' | 'enum-one' | 'date-max' | 'number';

export interface TableFilterOption {
  value: string;
  label: string;
}

/** One filterable field, derived from a table column. */
export interface TableFilterField {
  id: string;
  label: string;
  type: TableFilterFieldType;
  /** The distinct values found in the loaded rows. Empty for a `text` field. */
  options: TableFilterOption[];
  /**
   * The operators this field offers, in the order the operator select should list them. Absent
   * means "whatever the type offers" — the type-derived default every field had before this
   * existed. A field declares this when it can express fewer operators than its type otherwise
   * would, e.g. an `enum-one` field the endpoint only ever equates against.
   */
  operators?: FilterOperator[];
  /**
   * An example of what to type into a free-text or number value, in place of the generic "Enter a
   * value" — a field whose format is not obvious (a time, a duration in minutes) needs one
   * (HS-1440 QA). For `between` it fills the lower bound.
   */
  placeholder?: string;
  /** The upper bound's example under `between`. Falls back to `placeholder`. */
  upperPlaceholder?: string;
}

export interface TableFilterFieldOverrides {
  /** Column ids to leave out of the field list. */
  exclude?: readonly string[];
  /** Replace a column's header with different filter copy. */
  labels?: Readonly<Partial<Record<string, string>>>;
  /** Pin a field's control instead of inferring it from the loaded rows. */
  types?: Readonly<Partial<Record<string, TableFilterFieldType>>>;
  /** Restrict a field to fewer operators than its type would otherwise offer. */
  operators?: Readonly<Partial<Record<string, FilterOperator[]>>>;
  /**
   * Replace a field's derived vocabulary.
   *
   * Two kinds of field need this. One whose real options come from an endpoint rather than from the
   * rows on screen — server-driven pagination means the loaded page holds three of the six
   * institutes, so the count the design shows would be wrong. And one whose displayed text differs
   * from the value a query needs: the certification column reads `Certified`, the parameter takes
   * `true`. Supplying options makes the field an enumeration unless `types` says otherwise.
   */
  options?: Readonly<Partial<Record<string, TableFilterOption[]>>>;
  /**
   * Fields the builder should offer that are not columns.
   *
   * The design's own field list is proof this is needed: it offers `Group` and `User role` on a table
   * that shows neither. Devices is the practical case — `GetApiDeviceParams` filters on institution
   * and online status, and the table displays neither as a column, so without this the two filters
   * that work today would have nowhere to live. They are appended after the derived fields.
   */
  extra?: readonly TableFilterField[];
}

/**
 * The plain text a column holds for a row.
 *
 * `value` exists precisely so search and sort can read a column whose `cell` returns JSX, so it is
 * the first choice. A `cell` that returns a bare string or number — `cell: (row) => row.email` — is
 * just as readable, which covers the columns that never needed a `value`. Anything else renders only
 * JSX and has no text to compare, so the column is not filterable.
 */
function readCell<T>(column: TableColumn<T>, row: T): string | undefined {
  if (column.value) return column.value(row);
  const rendered = column.cell(row);
  if (typeof rendered === 'string') return rendered;
  if (typeof rendered === 'number') return String(rendered);
  return undefined;
}

/**
 * Infer the control from the data.
 *
 * A column whose every row differs — a name, an email — is free text: offering its ten current
 * values as a closed list would be worse than an input. A column that repeats values is an
 * enumeration, and those repeated values are its vocabulary.
 */
/** One value cannot show repetition, so there is nothing to infer an enumeration from. */
const MIN_VALUES_TO_INFER_ENUM = 2;

function inferType(values: string[]): TableFilterFieldType {
  const distinct = new Set(values);
  if (values.length < MIN_VALUES_TO_INFER_ENUM) return 'text';
  return distinct.size === values.length ? 'text' : 'enum';
}

/**
 * Derive a table's filterable fields from the columns it already declares (HS-1154 follow-up).
 *
 * Every list page describes its columns with a header and a text accessor, which is all a filter
 * field needs, so no page has to maintain a second parallel description of itself. The trailing
 * count the design shows beside a field — `Institute 6` — is the size of that field's vocabulary.
 *
 * **The inferred control depends on the rows currently loaded.** Pagination is server-driven, so a
 * page of data that happens to repeat no value reads as free text. `overrides.types` pins a field
 * whose data misleads the inference; nothing else about the hook needs configuring.
 *
 * Pass `overrides` from a `useMemo`, the same rule the repository already applies to `Select`
 * options: a fresh object each render defeats the memo and rebuilds every field on every keystroke.
 */
export function useTableFilterFields<T>(
  columns: TableColumn<T>[],
  rows: T[],
  overrides?: TableFilterFieldOverrides,
): TableFilterField[] {
  return useMemo(() => {
    const {
      exclude = [],
      labels = {},
      types = {},
      options: given = {},
      operators = {},
      extra = [],
    } = overrides ?? {};
    const excluded = new Set(exclude);

    const fields: TableFilterField[] = [];
    for (const column of columns) {
      if (excluded.has(column.id)) continue;
      const label = labels[column.id] ?? (typeof column.header === 'string' ? column.header : '');
      // A column whose header is a node carries no copy a filter row could show.
      if (label === '') continue;

      const supplied = given[column.id];
      const pinned = types[column.id];
      const values = rows
        .map((row) => readCell(column, row))
        .filter((value): value is string => value != null && value !== '');
      // Every row rendered JSX only, or held nothing, so there is nothing to compare against. Both
      // a supplied vocabulary and a pinned type override that: they are the page stating what the
      // field is, which does not depend on what the loaded page happens to contain. Without the
      // pinned case a date filter vanished from Users whenever no loaded user had a deactivation
      // date — the rows a filter exists to find are exactly the ones not on screen.
      if (supplied == null && pinned === undefined && rows.length > 0 && values.length === 0) {
        continue;
      }

      const type = pinned ?? (supplied != null ? 'enum' : inferType(values));
      const derived = [...new Set(values)]
        .sort((a, b) => a.localeCompare(b))
        .map((value) => ({ value, label: value }));
      // `enum-one` shares `enum`'s vocabulary, differing only in how many of it a row may pick —
      // that distinction belongs to the control the field renders as, not to how options are built.
      const isEnumeration = type === 'enum' || type === 'enum-one';
      const options = isEnumeration ? (supplied ?? derived) : [];
      const operatorOverride = operators[column.id];
      fields.push(
        operatorOverride
          ? { id: column.id, label, type, options, operators: operatorOverride }
          : { id: column.id, label, type, options },
      );
    }
    for (const field of extra) {
      if (!excluded.has(field.id)) fields.push(field);
    }
    return fields;
  }, [columns, rows, overrides]);
}
